import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { ReferralBonusTypes } from "@src/appV2/Accounts/WorkerReferrals/types";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useEffect } from "react";

import { useGetReferrals } from "../api/useGetReferrals";
import { HAS_SHOWN_REFERRAL_ACTIVE_MODAL_STORAGE_KEY } from "./constants";

interface ReferralData {
  bonusValue: number | undefined;
  bonusType: string | undefined;
  bonusRequirementMessage: string | undefined;
  bonusExpectationMessage: string | undefined;
}

interface UseShowReferralActiveBottomSheetResponse {
  referralData: ReferralData | undefined;
  referralActiveModalState: UseModalState;
}

export function useShowReferralActiveBottomSheet(): UseShowReferralActiveBottomSheetResponse {
  const referralActiveModalState = useModalState("closed");
  const [hasShownModal, setHasShownReferralActiveModal] = useLocalStorage(
    HAS_SHOWN_REFERRAL_ACTIVE_MODAL_STORAGE_KEY,
    false
  );

  const { data: referrals } = useGetReferrals({
    enabled: !hasShownModal,
  });

  const unpaidReferralActivationBonus = referrals?.find(
    (referral) =>
      !referral.bonusPaid &&
      referral.bonusType === ReferralBonusTypes.ACTIVATION &&
      isDefined(referral.bonusValue)
  );

  useEffect(() => {
    const shouldShowBottomSheet = unpaidReferralActivationBonus && !hasShownModal;

    if (shouldShowBottomSheet) {
      referralActiveModalState.openModal();
      setHasShownReferralActiveModal(true);
    }
  }, [
    unpaidReferralActivationBonus,
    referralActiveModalState,
    hasShownModal,
    setHasShownReferralActiveModal,
  ]);

  return {
    referralData: unpaidReferralActivationBonus
      ? {
          bonusValue: unpaidReferralActivationBonus.bonusValue,
          bonusType: unpaidReferralActivationBonus.bonusType,
          bonusRequirementMessage: unpaidReferralActivationBonus.bonusRequirementMessage,
          bonusExpectationMessage: unpaidReferralActivationBonus.bonusExpectationMessage,
        }
      : undefined,
    referralActiveModalState,
  };
}
